// Column — boards ToileArmed / ToileArmedScratch, verbatim values.

// z-index: `--tl-z-arena` (products/toile/styles/index.scss — the wall's stacking order, one source).
.stage {
    position: fixed;
    inset: 0;
    z-index: var(--tl-z-arena);
}

.scrim {
    position: absolute;
    inset: 0;
    background: var(--tl-scrim);
}

.esc {
    position: absolute;
    top: 18px;
    left: 22px;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 10px;
    background: color-mix(in srgb, var(--tl-surface) 92%, transparent);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-pill);
    padding: 7px 15px;
    font-size: 12.5px;
    color: var(--tl-muted);
    font-family: var(--tl-font);
    // A label, not a control: it says "click the wall", so a press on it must reach the wall.
    pointer-events: none;
}

.key {
    font-family: var(--tl-font-label);
    font-size: 9.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--tl-dim);
    border: 1px solid var(--tl-border);
    border-radius: var(--tl-radius-art-md);
    padding: 2px 6px;
}

.sep {
    color: var(--tl-dim);
}

// `inset: 0` makes this centring container cover the whole stage, so it — not the scrim beneath —
// received every click on the dimmed wall, and "or click the wall" (the strip's own words, BRIEF
// §6) did nothing (N13). The container is transparent to the pointer; its actual contents are not —
// two levels down now (`.pictureRegion`/`.dock`, plan `2026_09_18_armed_frame_budget.md` § D2, are
// themselves pointer-transparent wrappers, so the rule reaches their own children instead of them).
//
// Two children, not a single centred stack (§ D2): `.pictureRegion` takes the leftover height and
// centres the picture in it; `.dock` holds the bar at a fixed distance from the padding box, so
// nothing that opens or resizes above it (a fold, the muse strip growing the bar taller) moves the
// bar itself — only `.pictureRegion` shrinks to make room, since it is the only flexible child.
// Top/bottom padding come in as custom properties from `frame.ts`'s own chrome constants
// (`Column.tsx`), not restated here — the SAME numbers that produced the column's height budget.
.col {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    padding: var(--wall-col-top) 16px var(--wall-col-bottom);
    pointer-events: none;

    > * > * {
        pointer-events: auto;
    }
}

.pictureRegion {
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
}

// The bar's own dock (§ D2) — anchored `margin-top` below `.pictureRegion` rather than a shared
// `gap` on `.col`, since the two are no longer siblings in one flex run. Sized to the bar's own
// width (`--wall-bar-w`, § D5) and centred by `align-self` against `.col`'s own stretch default —
// `position: relative` is what lets the fold slots below paint OVER the frame instead of pushing it
// (§ D4).
.dock {
    position: relative;
    align-self: center;
    width: var(--wall-bar-w);
    max-width: 90vw;
    margin-top: 14px;
}

// The caption — the armed chain's words, centred over the picture (board `WordsAsDoors`: "the
// doors alone, in the caption's place… centred over the picture like the sheet's cap and the
// counter" — a drawing defect the first pass had, fixed 2026-09-18). Doors, when the chain has any
// (`WordDoors`'s own `.row`, this class bounding its width); a plain-text `.plain` fallback
// otherwise, same face and size, holding neither door nor link. `.pictureRegion`'s own
// `align-items: center` already centres this horizontally — nothing to restate here.
.caption {
    justify-content: center;
    max-width: 92%;
}

.plain {
    font-family: var(--tl-font-label);
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tl-muted);
    // As a <Link> (a chip-less tile with a pack page): the same dress, brightening on hover.
    text-decoration: none;
    transition: color 150ms ease;

    &:is(a):hover,
    &:is(a):focus-visible {
        color: var(--tl-text);
    }
}

// The frame's own wrapper (plan `2026_09_18_armed_frame_budget.md` § D3, its own beside-placement
// class superseded 2026-09-18 by the split counter — see `Counter.module.scss`'s `.arrowLeft`/
// `.arrowRight`/`.pill`) — sized to nothing of its own (no ring, no `overflow: hidden`; those stay
// on `.result`), it exists only so the counter's split pieces can position against the frame as
// absolutely-positioned siblings rather than reaching past the frame's own box. Its own box is
// exactly the frame's, since it carries no padding or border.
.frameWrap {
    position: relative;
}

// Width AND height are set inline, in pixels, by the box fit (`frame.fitBox` from
// `chains.armedAspect`, N14 + the 2026-08-31 sizing decision): the frame is the largest box of the
// picture's own ratio that fits the bar's width x the column's own height budget (`frame.ts`'s
// `armedFrame`), so a 21:9 result fills the bar and a 9:16 one stands height-bounded — no CSS width
// to fight the ratio, and the ring below wraps the frame itself rather than a fixed box the picture
// sits inside.
.result {
    border-radius: var(--tl-radius-art);
    overflow: hidden;
    background: var(--tl-surface);
    box-shadow:
        0 0 0 1px color-mix(in srgb, var(--tl-accent) 90%, transparent),
        0 0 44px color-mix(in srgb, var(--tl-accent) 28%, transparent),
        0 24px 60px color-mix(in srgb, var(--tl-shadow-ink) 60%, transparent);
    position: relative;
    // The cell at work (`WorkingCell.module.scss`, rendered inside while `working`) sizes its
    // place numeral off the box it stands in via `cqw` — this establishes that box the same way
    // `Tile.module.scss`'s `.tile` does for the wall cell.
    container-type: inline-size;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
}

.resultWorking {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--tl-accent) 50%, transparent);
}

.resultScratch {
    border: 1px dashed color-mix(in srgb, var(--tl-accent) 45%, transparent);
    background: color-mix(in srgb, var(--tl-accent) 4%, transparent);
    box-shadow: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px;
    text-align: center;
    color: var(--tl-accent);

    span {
        font-family: var(--tl-font);
        font-size: 15px;
        line-height: 1.35;
        max-width: 80%;
    }

    // The wall's rule returning under the cell's own act — the primary text colour, never the
    // accent, the same split of tones the ghost keeps at rest.
    .scratchRule {
        color: var(--tl-text);
    }
}

// The terms gate's card OVERLAYS the frame's lower edge rather than pushing it (plan
// `2026_09_18_armed_frame_budget.md` § D4: "folds overlay, they do not push"), painted above the bar
// inside `.dock` (`position: relative`) at the stack's own gap. The card is full-width, not a door
// under a chip. The ratio row no longer shares this overlay (dev fix 2026-09-18: surfaceless pills
// sitting on the picture's lower edge were unreadable over a light picture, and landed exactly where
// the counter's own new corner pill stands) — it unfolds in the bar's own slot instead, `.ratioSlot`
// below.
.consentSlot {
    position: absolute;
    bottom: calc(100% + 14px);
    width: var(--wall-bar-w);
    max-width: 90vw;
}

.bar {
    width: var(--wall-bar-w);
    max-width: 90vw;
    display: flex;
    align-items: center;
    gap: 12px;
    background: color-mix(in srgb, var(--tl-surface) 92%, transparent);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-container);
    padding: 10px 10px 10px 18px;
}

.slot {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

// The bar's identity slot (plan `2026_09_18_ovid_wall_polish.md` § P3/P4, board `OvidArmed.dc.html`
// `.rbar`'s `.thumbs`) — the house's own chip lives entirely inside its panel component
// (`MuseStrip.module.scss`); this is only the bar's own layout wrapper, sized to its content rather
// than the field's `flex: 1`, so `.spacer` is what pushes the pencil and Generate to the right.
.idChip {
    display: flex;
    align-items: center;
    min-width: 0;
    // The slot takes every pixel between the bar's edge and the pencil (operator ruling 2026-09-22,
    // over the board's half-bar split with a spacer): a panel that draws a track (Narcis's
    // training state) runs it to the verb; a chip that draws only a thumb and a name is
    // left-aligned in it and reads exactly as before.
    flex: 1;
}

// The bar's OPEN identity slot (plan `2026_09_18_armed_frame_budget.md` § D6, board
// `OvidMuseFlow.dc.html`'s `.muses`) — the choosing strip takes the field's own `flex: 1` in its
// place; no pencil, no spacer stand beside it (`Column.tsx` withholds both while this slot shows).
.idOpen {
    flex: 1;
    min-width: 0;
    display: flex;
}

// The bar's OPEN ratio slot (dev fix 2026-09-18, superseding plan `2026_09_18_armed_frame_budget.md`
// § D4 for this row — see `Column.tsx`'s own header and `RatioFold.tsx`'s): the pills take the
// field's own `flex: 1` in its place, right-aligned toward Generate the way the row's old overlay
// alignment did, since nothing here shares a line with the field's own left edge any more. No
// pencil, no ratio chip, no identity chip stand beside it (`Column.tsx` withholds all three while
// this slot shows) — same rule `.idOpen` already carries for the identity panel's own full turn.
.ratioSlot {
    flex: 1;
    min-width: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    // A narrow window (`max-width: 90vw` on the bar) can be narrower than six pills — `.ratioPills`
    // below never shrinks (`flex: none`), so this scrolls horizontally instead of wrapping them.
    // `padding`/`margin` are `.track`'s own cure (`products/ovid/wall/MuseStrip.module.scss`, fixed
    // the same day): a scroll box clips whatever a focused pill's own outline draws outside its
    // padding box, and the equal negative margin keeps the bar's height where it was.
    overflow-x: auto;
    scrollbar-width: none;
    padding: 3px;
    margin: -3px;

    &::-webkit-scrollbar {
        display: none;
    }
}

// `RatioRow`'s own `.row` (`RatioFold.module.scss`) wraps by default (`flex-wrap: wrap`) — the right
// shape for the sheet's in-flow row, wrong for this one. `flex: none` keeps `.row` at its natural
// content width instead of letting `.ratioSlot`'s own flexbox shrink it down to fit, which is what
// would force it to wrap internally; content-width pills never need to, so `.ratioSlot`'s own
// scrollbar is what narrows the window instead. Overriding `flex-wrap` itself would need it to win
// the cascade against `.row`'s own declaration from a DIFFERENT compiled stylesheet — the exact
// bundle-order hazard commit `951cbbf95` paid down — so this sidesteps that question rather than
// re-opening it.
.ratioPills {
    flex: none;
}

// The pencil door (board `.ico`) — a real button, square, matching the verb's own corner radius
// rather than the board's flat one (the shared chrome is rounded everywhere else in this file).
// The door is dressed through `--tl-door*`, which every house defines (Narcis: iris; Toile and Ovid the
// text-white dress).
.pencil {
    flex: none;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--tl-door-border);
    border-radius: var(--tl-radius-chrome);
    background: var(--tl-door-bg);
    color: var(--tl-door);
    cursor: pointer;

    &:hover {
        border-color: var(--tl-door-hover);
    }

    &:focus-visible {
        outline: 1px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

// The tungsten caret is the browser's own (N15): a decorative bar beside a native caret read as two
// carets in the field. `caret-color` gives the real one the accent token, and with it the blink,
// the movement, the selection behaviour and the reduced-motion respect no drawn bar can have.
.input {
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    outline: none;
    caret-color: var(--tl-accent);
    font-family: var(--tl-font);
    font-size: 14px;
    color: var(--tl-text);

    &::placeholder {
        color: var(--tl-dim);
    }

    // A touch device at column width (a tablet): the same focus-zoom guard as the sheet's field.
    @media (pointer: coarse) {
        font-size: 16px;
    }
}

.verb {
    background: var(--tl-accent);
    color: var(--tl-accent-ink);
    font-family: var(--tl-font);
    font-weight: 500;
    font-size: 13.5px;
    padding: 10px 20px;
    border: none;
    border-radius: var(--tl-radius-chrome);
    white-space: nowrap;
    box-shadow: 0 0 24px color-mix(in srgb, var(--tl-accent) 35%, transparent);
    cursor: pointer;
}

// The line (BRIEF §11's vocabulary) while the column is open — an error that did not land, or the
// state of the chain being worked on. Colour and case are the Notice component's job now
// (`@core/ui/Notice`) — this class carries position/type only.
.note {
    margin: 10px 0 0;
    font-family: var(--tl-font-label);
    font-size: 10px;
    letter-spacing: 0.16em;
    text-align: center;
}

// The cell at work's own reduced-motion rule lives with the rest of it, in
// `WorkingCell.module.scss` — nothing animates in this file any more (the caption's own discreet ↗
// carries no nudge, board A trimmed 2026-09-18).
